import type { BookingFailure, BookingOutcome } from "../booking/operations";
import type { CheckoutStart } from "../payments/checkoutStart";
import { PAYMENTS_ENABLED } from "../../config/payments";
import { interpret, type BookingResult } from "../booking/useBooking";
import { CHECKOUT_EXPIRED_REPLAY_IS_DEFINITE } from "../booking/replayDefinite";
import type { Call, CodeRequestAnswer, CodeVerifyAnswer, Instant } from "./answers";
import type { CheckoutAttempt, GuestSubmission } from "./checkout";
import { acceptManagementUrl } from "./managementUrl";
import { CHECKOUT_MARKER_MAX_MS, CHECKOUT_MARKER_SLACK_MS, CLAIM_CEILING_MS, UNKNOWN_OUTCOME_CLAIM_MS } from "../../lib/guestSession";
import type { SubmitResult } from "./port";
import { definiteFrom, pastEvery, refusalCanBeDefinite, type SendFacts } from "../booking/replayRule";
import { sameTerms, type TermsDecision, type TermsRefusal } from "../terms/terms";
import type { BookingCancellationPolicyDisplay } from "../profile/operations";

/**
 * THE GUEST CHECKOUT, AS STATES BACKEND PUTS IT IN.
 *
 * Pure: every transition is `reduce(state, event)`, with the clock passed in
 * on the events that need it. Nothing here waits, retries, or sends; the hook
 * does that, and it can only ask for what a state allows.
 *
 * THE RULES THIS FILE HOLDS, each asserted through the hook in
 * useGuestCheckout.test.ts (and in the browser in tests/e2e/book-harness/):
 *
 *  1. One key per checkout, established before the first code request, kept
 *     through verification, submission, and every same-key check.
 *  2. A submission is frozen at its first dispatch. While its outcome is
 *     unknown, the ONLY thing that may be sent is that same submission. No
 *     edit, no new code, no new hold, no new key.
 *  3. A submission is released only by an answer the contract documents as
 *     "no appointment was written" — never by a timeout, a lost answer, a
 *     refresh of the hold, or the person changing their mind.
 *  4. Every wait and deadline is a server value, stored as the instant it
 *     names. Nothing is counted down from a number authored here.
 *  5. Accepted is not delivered. SENT means the provider took the message.
 *  6. A hold that ran out is not a time that was taken.
 *  7. REQUESTED and CONFIRMED are different outcomes and stay different.
 *  8. Guest verification off, or delivery unavailable, FAILS CLOSED. There is
 *     no path from here to the legacy unverified checkout.
 */

export interface Challenge {
  readonly verificationId: string;
  readonly maskedEmail: string;
  readonly expiresAt: Instant;
  readonly resendAfter: Instant;
}

export interface Proof {
  readonly verificationId: string;
  readonly proofExpiresAt: Instant;
}

/** What sits on the code screen besides the field. At most one at a time. */
export type CodeNote =
  | { readonly kind: "none" }
  | { readonly kind: "incorrect"; readonly attemptsRemaining: number | null }
  | { readonly kind: "expired" }
  | { readonly kind: "exhausted" }
  /** Verify was rate limited; `retryAt` is receipt time + Backend's seconds. */
  | { readonly kind: "verify-limited"; readonly retryAt: number }
  /** Verify's answer was lost. Verifying is idempotent server-side, so asking again is safe. */
  | { readonly kind: "verify-lost" }
  /** A resend was refused as too soon; the control follows `challenge.resendAfter`. */
  | { readonly kind: "resend-too-soon" }
  | { readonly kind: "resend-limited"; readonly retryAt: number }
  /** A resend's answer was lost: a new code may or may not be on its way. */
  | { readonly kind: "resend-lost" }
  /** The challenge is gone server-side; a new code is the only way on. */
  | { readonly kind: "challenge-gone" }
  /** createBooking refused the proof (GUEST_CONTACT_UNVERIFIED). */
  | { readonly kind: "unverified-at-submit" };

export type UnavailableReason = "verification-off" | "delivery-unavailable" | "guest-booking-unavailable";

export type Phase =
  /** Nothing requested yet: the review step, whose primary action asks for a code. */
  | { readonly name: "entry" }
  /** `resend` keeps the code screen on screen while a new code is asked for. */
  | { readonly name: "requesting"; readonly resend: boolean }
  /** The first request's answer never arrived. Nothing is claimed; asking again is the person's call. */
  | { readonly name: "request-lost" }
  /**
   * `too-soon`: Backend's resend wait (RESEND_TOO_SOON with no challenge held).
   * `rate-limited`: the limiter (RATE_LIMITED). They read differently: a wait
   * is not "too many requests".
   */
  | { readonly name: "request-limited"; readonly retryAt: number; readonly reason: "too-soon" | "rate-limited" }
  /** The answer broke the contract's own rules. Fail closed; claim nothing. */
  | { readonly name: "request-malformed" }
  | { readonly name: "code"; readonly note: CodeNote }
  | { readonly name: "verifying" }
  /** Verified; the proof is held and the booking has not been sent. */
  | { readonly name: "ready" }
  | { readonly name: "change-email" }
  | { readonly name: "submitting" }
  | {
      readonly name: "uncertain";
      readonly checking: boolean;
      readonly checkAlsoLost: boolean;
      /** False once the identity the key belongs to is gone: nothing can reach the attempt. */
      readonly canCheck: boolean;
      /**
       * Until when this unknown outcome keeps the identity claimed: entering
       * it plus `UNKNOWN_OUTCOME_CLAIM_MS`, renewed by each Check press
       * (security audit N1). An instant, stored as the machine stores every
       * deadline; nothing here counts it down.
       */
      readonly claimUntil: number;
      /** The Check in flight: when it went and under which identity (rc.11 1c). */
      readonly checkSent?: CheckSent;
      /** The server answered CHECKOUT_EXPIRED, not yet definite: the copy is pay.return.waiting. */
      readonly payWaiting?: true;
    }
  | {
      readonly name: "done";
      /** The in-person pairs, or (booking-v1 1.20.0, payments gate open) an app-paid outcome. */
      readonly kind: BookingResult["kind"];
      readonly outcome: BookingOutcome;
      /** Validated, or null. Null on the normal guest path is a go-live defect, not a steady state. */
      readonly managementUrl: string | null;
    }
  /**
   * booking-v1 1.20.0, payments gate open: an app-paid booking waiting for its
   * payment (useBooking `interpret`'s "pay"). Not done: nothing is booked until
   * Stripe reports the payment. The hook leaves for Stripe from the press's
   * own answer only (W-1); the screen's button is a new press.
   */
  | {
      readonly name: "pay";
      readonly start: CheckoutStart;
      readonly outcome: BookingOutcome;
      /** Validated, or null, as for `done`: where the guest finds the booking after paying. */
      readonly managementUrl: string | null;
    }
  | { readonly name: "unavailable"; readonly reason: UnavailableReason }
  /** The anonymous session the key was bound to is gone, before anything was sent. */
  | { readonly name: "identity-lost" }
  | { readonly name: "booking-failed"; readonly failure: BookingFailure }
  | { readonly name: "unauthorized" }
  /** Backend says this key already booked, and this page holds no submission to replay. */
  | { readonly name: "already-booked-unreachable" }
  /** No key could be minted (no WebCrypto) or identity failed before anything was sent. */
  | { readonly name: "cannot-start" };

export interface GuestCheckoutState {
  readonly phase: Phase;
  readonly attempt: CheckoutAttempt | null;
  /** The exact string the current challenge was requested for. */
  readonly email: string | null;
  readonly challenge: Challenge | null;
  readonly proof: Proof | null;
  /** Frozen at first dispatch. Non-null means: this is the only thing that may be sent. */
  readonly submission: GuestSubmission | null;
  /** The appointment id this checkout has already been told about, for replay consistency. */
  readonly knownAppointmentId: string | null;
  /**
   * The hold ran out before anything was sent. A FLAG, not a phase: a code
   * request or a verification already in flight still lands, and its answer
   * is kept. What it blocks is submitting, until a new hold arrives.
   */
  readonly holdExpired: boolean;
  /**
   * The anonymous session could not be READ on the last tap. Nothing was sent
   * by it, the checkout is intact, and trying again is safe. A FLAG, not a
   * phase: the person stays exactly where they were — `ready` stays `ready`,
   * an unknown outcome stays unknown — and the screen says why the tap did
   * nothing. (QE A2-3, reviewer P2 and P3: it used to be silent everywhere
   * but the code step, and it sent `ready` back to the code step.)
   */
  readonly sessionUnavailable: boolean;
  /**
   * The booking refused a CONTACT FIELD (INVALID_INPUT naming
   * `guestContact.*`), e.g. a name the display-name rules reject. Backend
   * (b77e6beb, 2026-09-23): the proof binds the EMAIL only, the refusal writes
   * no receipt, and the proof is not consumed. So the guest corrects the field
   * and books under the SAME proof and key, with no new code. A new code is
   * needed only if the proof has expired, which the server answers with
   * GUEST_CONTACT_UNVERIFIED. The flow reads this once, sends the guest to
   * Details with the field flagged, and acknowledges it (CONTACT_REJECTION_SEEN).
   */
  readonly contactRejection: { readonly field: string | null; readonly detailCode: string | null } | null;
  /**
   * WHAT THE GUEST DECIDED ABOUT LURA'S TERMS, captured on Review BEFORE the
   * code is requested (REQUEST_STARTED carries it) and frozen into the
   * submission with the proof: one decision, made once, sent with the booking.
   * `{ acceptance: null }` means no Terms were published when Review read
   * them. Null means no decision is held, and nothing may be requested or
   * submitted until there is one.
   */
  readonly terms: TermsDecision | null;
  /**
   * THE CANCELLATION POLICY THE GUEST AGREED TO ON REVIEW (rc.10), captured
   * with the Terms decision when Continue was pressed and cleared with it. The
   * code step's agreement opens it ("shown on the review screen"), never the
   * live profile. In memory only: never sent, never stored, never logged.
   */
  readonly policy: BookingCancellationPolicyDisplay | null;
  /**
   * rc.10: the policy changed between Review and Verify. The decision and its
   * snapshot were cleared and the guest is back on Review with the new policy
   * and a notice. The challenge is KEPT (Q2): a fresh Continue returns to the
   * code step with no new email while the code is still valid.
   */
  readonly policyChanged: boolean;
  /**
   * Backend refused the Terms the submission carried (TERMS_STALE,
   * TERMS_ACCEPTANCE_REQUIRED, or TERMS_UNAVAILABLE). A pre-write refusal:
   * the submission is released and the decision cleared, but the KEY and the
   * PROOF are kept (termsAcceptance is outside the fingerprint, and
   * assertGuestProofUsable does not consume the proof). The guest agrees again
   * on Review, and the next submission re-asks under the same key and proof,
   * so a booking that key committed replays. A new object per refusal, so the
   * flow can react to each one.
   */
  readonly termsRefusal: {
    readonly refusal: TermsRefusal;
    /**
     * The refusal answered a CHECK after a lost answer: the first send may
     * have committed. The "may have gone through" notice stays up with the
     * Terms re-ask beneath it (Web Director, audit WEB-TOS-03), and a hold
     * that runs out does not become "No booking was made".
     */
    readonly afterCheck: boolean;
  } | null;
  /**
   * THE REQUEST THAT MAY HAVE COMMITTED, kept after it was released.
   *
   * A Terms refusal that answers a CHECK releases the submission (the guest
   * must agree again), but the first send may still have been inside its
   * transaction. If a later submission under the same key is then answered
   * IDEMPOTENCY_CONFLICT, that booking DID commit, and only this original
   * request replays it (termsAcceptance is outside the fingerprint). Kept
   * for exactly that; never sent otherwise (security re-audit, rc.8).
   */
  readonly priorSubmission: GuestSubmission | null;
  /**
   * The latest CHECK whose answer was lost (rc.11 1c): a Check is itself a
   * full send that may still commit, so its threshold counts too. A lost
   * Check with no recorded instant leaves facts that are never definite.
   */
  readonly latestLostSend: SendFacts | null;
}

export interface Policy {
  /**
   * May a proof verified under one hold be spent under a new one? The draft
   * design says yes (departure D1); the Web Director has ruled it stays OFF
   * until the published contract states it. Off means: after a new hold, a
   * new code.
   */
  readonly proofSurvivesNewHold: boolean;
}

export const DEFAULT_POLICY: Policy = Object.freeze({ proofSurvivesNewHold: false });

export const INITIAL: GuestCheckoutState = Object.freeze({
  phase: { name: "entry" } as Phase,
  attempt: null,
  email: null,
  challenge: null,
  proof: null,
  submission: null,
  knownAppointmentId: null,
  holdExpired: false,
  sessionUnavailable: false,
  contactRejection: null,
  terms: null,
  policy: null,
  policyChanged: false,
  termsRefusal: null,
  latestLostSend: null,
  priorSubmission: null,
});

export type Event =
  /**
   * `terms`: the decision made on Review for this request. Absent on a resend or
   * a changed address, which keep the decision already held.
   */
  | { type: "REQUEST_STARTED"; attempt: CheckoutAttempt; email: string; terms?: TermsDecision; policy?: BookingCancellationPolicyDisplay | null }
  /** After a Terms refusal, with the proof still held: the guest agreed again on Review. */
  | { type: "TERMS_ACCEPTED"; terms: TermsDecision; policy?: BookingCancellationPolicyDisplay | null }
  /** rc.10: the re-read before Verify found a different policy. Nothing was sent. */
  | { type: "POLICY_CHANGED" }
  | { type: "REQUEST_FINISHED"; call: Call<CodeRequestAnswer>; now: number }
  | { type: "VERIFY_STARTED" }
  | { type: "VERIFY_FINISHED"; call: Call<CodeVerifyAnswer>; now: number }
  | { type: "EDIT_EMAIL"; now: number }
  | { type: "EDIT_CANCELLED" }
  | { type: "SUBMIT_STARTED"; submission: GuestSubmission }
  | { type: "SUBMIT_FINISHED"; result: SubmitResult; webHost: string; now: number }
  /** `mono` and `uid`: the replay's send instant and identity (rc.11 1c); absent, never definite. */
  | { type: "CHECK_STARTED"; now: number; mono?: number; uid?: string }
  | { type: "CHECK_FINISHED"; result: SubmitResult; webHost: string; /** When the answer came (a payment step's deadline is read against it). */ now?: number }
  /** The identity no longer matches the attempt's uid. */
  | { type: "IDENTITY_LOST" }
  | { type: "START_FAILED" }
  | { type: "HOLD_EXPIRED"; now: number }
  | { type: "NEW_HOLD"; now: number; policy: Policy }
  | { type: "RESTART"; now: number }
  /** The address on the Details step changed (the guest went Back and edited it). */
  | { type: "DETAILS_CHANGED"; email: string; now: number }
  | { type: "CONTACT_REJECTION_SEEN" };

/** A dispatched submission whose outcome is not yet known. Rule 2 lives here. */
export function isLocked(state: GuestCheckoutState): boolean {
  return (
    state.submission !== null &&
    (state.phase.name === "submitting" || state.phase.name === "uncertain")
  );
}

/**
 * AN ATTEMPT PAUSE: verify answered RATE_LIMITED and Backend's instant has not
 * passed. During it every next step is a GRANT (web v2 §03), and the contract
 * returns none (readiness Q-B10). A new code or a new address would each start
 * a fresh challenge beside the paused one, so neither is offered or accepted
 * until the pause lifts; the person waits for Backend's time, which the code
 * screen states.
 */
export function isVerifyPaused(state: GuestCheckoutState, now: number): boolean {
  const p = state.phase;
  return p.name === "code" && p.note.kind === "verify-limited" && now < p.note.retryAt;
}

/**
 * The phases in which a sign-in would lose something only this UID can
 * finish: a call in flight, or a booking whose outcome is unknown until its
 * same-UID Check. Every other phase either has no booking yet (a code, proof
 * or limiter window: the identity claim keeps the UID, but switching
 * accounts is allowed) or has a definite answer.
 */
// "pay" (booking-v1 1.20.0): the checkout is bound to this UID until it settles,
// so an identity change could strand the payment; it gates like the others.
const UNRESOLVED_PHASES: ReadonlySet<Phase["name"]> = new Set(["requesting", "verifying", "submitting", "uncertain", "pay"]);

/**
 * WHETHER AN IDENTITY CHANGE COULD LOSE A BOOKING (Book's identity-change and
 * stale-reload gates, GuestFlowHost). An original that may have committed and
 * is still held (`priorSubmission`: a Check's Terms refusal, then a later
 * definite refusal of the re-agreed send) is unresolved too, whatever the
 * phase shows: an identity change would lose its same-identity Check (review
 * of 6d53231b, P2).
 */
export function checkoutUnresolved(state: GuestCheckoutState): boolean {
  return UNRESOLVED_PHASES.has(state.phase.name) || state.priorSubmission !== null;
}

/** May a code (first or resend) be requested now? */
export function canRequestCode(state: GuestCheckoutState, now: number): boolean {
  if (isLocked(state)) return false;
  if (isVerifyPaused(state, now)) return false;
  const p = state.phase;
  if (p.name === "request-limited") return now >= p.retryAt;
  if (p.name === "code") {
    if (p.note.kind === "resend-limited" && now < p.note.retryAt) return false;
    if (!state.challenge) return true;
    return now >= Date.parse(state.challenge.resendAfter);
  }
  // A lost or malformed code request may be asked again: it is not a booking,
  // and a new request is a new generation that kills whatever the lost one did.
  return p.name === "entry" || p.name === "request-lost" || p.name === "request-malformed" || p.name === "change-email";
}

/** How long, and at most how long, the guest identity is claimed. */
export interface IdentityClaim {
  /** Epoch milliseconds; Infinity only while a call is on the wire. */
  readonly until: number;
  /** The documented ceiling of the server window behind `until` (`CLAIM_CEILING_MS`). */
  readonly maxMs: number;
}

const IN_FLIGHT: IdentityClaim = Object.freeze({ until: Number.POSITIVE_INFINITY, maxMs: Number.POSITIVE_INFINITY });

/**
 * HOW LONG THIS CHECKOUT NEEDS THE UID ITS KEY IS BOUND TO (security audit M1,
 * F1 and N1), or null when nothing this page can still do needs it. The guest
 * identity is claimed for exactly this long, so no rotation retires it under a
 * live code, proof or answer, and none is held off by a checkout that has
 * nothing left to do.
 *
 *  - Without a bound only while a call is on the wire (`requesting`,
 *    `verifying`, `submitting`).
 *  - An unknown outcome (`uncertain`, while its same-UID Check is still
 *    possible) until `claimUntil`: thirty minutes from entering it, renewed by
 *    each Check press. After that a Check fails safe (`identity-lost`).
 *  - A SERVER instant while something the server issued is live, capped at
 *    that window's documented ceiling (N4): the challenge on the code step,
 *    the proof when verified, Backend's wait on `request-limited`, and a kept
 *    challenge or proof on `entry` and `change-email` (not once the hold has
 *    ended: the next hold is claimed on its own, and a hold-ended screen is a
 *    decision, not a window). An unreadable instant is no claim.
 *  - null everywhere else. `done` and `already-booked-unreachable` are
 *    retired instead (auth/guestRotation.ts): a booking exists under the UID.
 *    The failures and fail-closed screens offer nothing that needs the UID:
 *    `request-lost` and `request-malformed` hold no challenge,
 *    `booking-failed` has no Check, and `unavailable`, `unauthorized`,
 *    `identity-lost` and `cannot-start` start over if at all.
 *
 * Exhaustive over the phases: a new phase does not compile until it is placed.
 */
export function identityClaim(state: GuestCheckoutState): IdentityClaim | null {
  if (state.attempt === null) return null;
  const p = state.phase;
  const challengeUntil = state.challenge ? instantMs(state.challenge.expiresAt) : null;
  const proofUntil = state.proof ? instantMs(state.proof.proofExpiresAt) : null;
  const code = challengeUntil === null ? null : { until: challengeUntil, maxMs: CLAIM_CEILING_MS.code };
  const proof = proofUntil === null ? null : { until: proofUntil, maxMs: CLAIM_CEILING_MS.proof };
  switch (p.name) {
    case "requesting":
    case "verifying":
    case "submitting":
      return IN_FLIGHT;
    case "uncertain":
      return p.canCheck && Number.isFinite(p.claimUntil)
        ? { until: p.claimUntil, maxMs: UNKNOWN_OUTCOME_CLAIM_MS }
        : null;
    case "code":
      return code;
    case "ready":
      return proof;
    case "entry":
    case "change-email":
      if (state.holdExpired) return null;
      if (!code) return proof;
      if (!proof) return code;
      return proof.until >= code.until ? proof : code;
    case "request-limited":
      return Number.isFinite(p.retryAt) ? { until: p.retryAt, maxMs: CLAIM_CEILING_MS.limiter } : null;
    case "pay":
      // The checkout binds to this UID (checkoutStatus answers only it), so
      // the payment step holds it as the checkout marker would: until the
      // server's checkout deadline plus the marker's slack, capped at the
      // marker's ceiling (checkout-identity-claim-design.md §2.1).
      return Number.isFinite(p.start.expiresAt)
        ? { until: p.start.expiresAt + CHECKOUT_MARKER_SLACK_MS, maxMs: CHECKOUT_MARKER_MAX_MS }
        : null;
    case "request-lost":
    case "request-malformed":
    case "done":
    case "unavailable":
    case "identity-lost":
    case "booking-failed":
    case "unauthorized":
    case "already-booked-unreachable":
    case "cannot-start":
      return null;
    default: {
      const unplaced: never = p;
      return unplaced;
    }
  }
}

function instantMs(value: Instant): number | null {
  const at = Date.parse(value);
  return Number.isFinite(at) ? at : null;
}

/** May the code field be submitted now? */
export function canVerify(state: GuestCheckoutState, now: number): boolean {
  if (state.phase.name !== "code" || !state.challenge) return false;
  const note = state.phase.note;
  if (note.kind === "verify-limited" && now < note.retryAt) return false;
  // A dead or spent challenge cannot be verified; a resend is the way on.
  return (
    note.kind !== "expired" &&
    note.kind !== "exhausted" &&
    note.kind !== "challenge-gone" &&
    note.kind !== "unverified-at-submit"
  );
}

function codeWith(state: GuestCheckoutState, note: CodeNote, patch: Partial<GuestCheckoutState> = {}): GuestCheckoutState {
  return { ...state, ...patch, phase: { name: "code", note } };
}

function afterRequest(state: GuestCheckoutState, call: Call<CodeRequestAnswer>, now: number): GuestCheckoutState {
  // Was this a resend from the code step, or the first request?
  const resend = state.challenge !== null;

  if (call.kind === "lost") {
    return resend ? codeWith(state, { kind: "resend-lost" }) : { ...state, phase: { name: "request-lost" } };
  }
  if (call.kind === "malformed") {
    return { ...state, phase: { name: "request-malformed" } };
  }
  const a = call.answer;
  switch (a.status) {
    case "SENT":
      // A new generation: any earlier code and proof are dead server-side.
      return codeWith(state, { kind: "none" }, {
        challenge: {
          verificationId: a.verificationId,
          maskedEmail: a.maskedEmail,
          expiresAt: a.expiresAt,
          resendAfter: a.resendAfter,
        },
        proof: null,
      });
    case "RESEND_TOO_SOON":
      if (state.challenge) {
        return codeWith(state, { kind: "resend-too-soon" }, {
          challenge: { ...state.challenge, resendAfter: a.resendAfter },
        });
      }
      // Too soon for a first request (a page that lost its memory, say): no
      // challenge is held, so there is nothing to verify. Wait, then ask.
      return { ...state, phase: { name: "request-limited", retryAt: Date.parse(a.resendAfter), reason: "too-soon" } };
    case "RATE_LIMITED": {
      const retryAt = now + a.retryAfterSeconds * 1000;
      return resend
        ? codeWith(state, { kind: "resend-limited", retryAt })
        : { ...state, phase: { name: "request-limited", retryAt, reason: "rate-limited" } };
    }
    case "DELIVERY_UNAVAILABLE":
      return { ...state, phase: { name: "unavailable", reason: "delivery-unavailable" } };
    case "UNAVAILABLE":
      return { ...state, phase: { name: "unavailable", reason: "verification-off" } };
    case "ATTEMPT_ALREADY_BOOKED":
      // Only the frozen submission may reach that booking (rule 2): the live
      // one, or the one a Check's Terms refusal released (`priorSubmission`,
      // rc.8), which is the request that booked. Without either, this page
      // cannot rebuild it and must not try.
      if (state.submission) {
        return { ...state, phase: { name: "uncertain", checking: false, checkAlsoLost: false, canCheck: true, claimUntil: now + UNKNOWN_OUTCOME_CLAIM_MS } };
      }
      if (state.priorSubmission) {
        return {
          ...state,
          submission: state.priorSubmission,
          priorSubmission: null,
          phase: { name: "uncertain", checking: false, checkAlsoLost: false, canCheck: true, claimUntil: now + UNKNOWN_OUTCOME_CLAIM_MS },
        };
      }
      return { ...state, phase: { name: "already-booked-unreachable" } };
  }
}

function afterVerify(state: GuestCheckoutState, call: Call<CodeVerifyAnswer>, now: number): GuestCheckoutState {
  if (call.kind === "lost" || call.kind === "malformed") return codeWith(state, { kind: "verify-lost" });
  const a = call.answer;
  switch (a.status) {
    case "VERIFIED":
    case "ALREADY_VERIFIED":
      if (!state.challenge) return codeWith(state, { kind: "challenge-gone" });
      return {
        ...state,
        proof: { verificationId: state.challenge.verificationId, proofExpiresAt: a.proofExpiresAt },
        phase: { name: "ready" },
      };
    case "CODE_INCORRECT":
      // Backend saying none are left is Backend saying the code step is spent:
      // Confirm would only earn ATTEMPTS_EXHAUSTED. Say so now. (QE A3.)
      return a.attemptsRemaining === 0
        ? codeWith(state, { kind: "exhausted" })
        : codeWith(state, { kind: "incorrect", attemptsRemaining: a.attemptsRemaining });
    case "CODE_EXPIRED":
      return codeWith(state, { kind: "expired" });
    case "ATTEMPTS_EXHAUSTED":
      return codeWith(state, { kind: "exhausted" });
    case "CHALLENGE_NOT_FOUND":
      return codeWith(state, { kind: "challenge-gone" }, { challenge: null, proof: null });
    case "RATE_LIMITED":
      return codeWith(state, { kind: "verify-limited", retryAt: now + a.retryAfterSeconds * 1000 });
  }
}

/*
 * rc.11: A REQUEST THAT MAY HAVE COMMITTED OUTRANKS A HOLD THAT ENDED.
 *
 * `priorSubmission` is the original request a Check's Terms refusal released
 * (rc.8). It may have booked. While it is held, "choose your time again"
 * would tell the person nothing happened when it may have; a reload would
 * then drop the key that reaches the booking. So an expiry, or a hold-gone
 * that answers a NEW request, becomes the uncertainty on the ORIGINAL: its
 * Check replays it, and only Backend's answer decides (committed: the booking;
 * hold-gone: nothing was made, and hold-ended is then true).
 */
function uncertainOnPrior(state: GuestCheckoutState, prior: GuestSubmission, claimUntil: number): GuestCheckoutState {
  return {
    ...state,
    submission: prior,
    priorSubmission: null,
    // No new send is possible on a dead hold: the decision made for one goes.
    terms: null,
    policy: null,
    policyChanged: false,
    termsRefusal: null,
    holdExpired: true,
    phase: { name: "uncertain", checking: false, checkAlsoLost: false, canCheck: true, claimUntil },
  };
}

/**
 * rc.11 (review P1): a hold that ended while a code was being asked for or
 * checked. Only the flag was set then; when the call lands, nothing can be
 * sent on the dead hold, so with the original held the checkout settles on its
 * uncertainty (never a Confirm that cannot send).
 */
function settleExpiredPrior(state: GuestCheckoutState, now: number): GuestCheckoutState {
  const p = state.phase.name;
  return state.holdExpired && state.priorSubmission && !state.submission && p !== "requesting" && p !== "verifying"
    ? uncertainOnPrior(state, state.priorSubmission, now + UNKNOWN_OUTCOME_CLAIM_MS)
    : state;
}

/** A Check's send: its monotonic and wall-clock instants and the identity it went under. */
export interface CheckSent {
  readonly mono: number;
  readonly at: number;
  readonly uid: string;
}

/*
 * rc.11 1c: WHEN A REPLAY'S REFUSAL MEANS "NO BOOKING UNDER THIS KEY". The
 * rule, its clock handling and its dependence on Backend are in
 * features/booking/replayRule.ts, shared with the signed-in booking.
 */
export { REPLAY_CLOCK_SKEW_PAD_MS, REPLAY_RECEIPT_PAD_MS, REPLAY_RUN_MS, refusalCanBeDefinite } from "../booking/replayRule";

/** The monotonic instant from which a replay of `submission` can be definite; null: never. */
export function replayDefiniteFrom(submission: GuestSubmission): number | null {
  return definiteFrom(submission.sent);
}

/**
 * Whether the Check now answered can be definite: (i) the same uid as every
 * request held under the key, and (ii) past the threshold of each of them and
 * of the latest lost Check. A refusal after the receipt read under the key
 * covers every request: had an older one committed, a newer would have met a
 * conflict there.
 */
export function replayIsDefinite(state: GuestCheckoutState, check: CheckSent | undefined): boolean {
  if (!state.submission || !check) return false;
  const held = [state.submission, state.priorSubmission].filter((s) => s !== null);
  if (held.some((s) => s.uid !== check.uid)) return false;
  return pastEvery(check.mono, [...held.map((s) => s.sent), ...(state.latestLostSend ? [state.latestLostSend] : [])]);
}

/** A Check of the exact original answered with a conflict: the Backend defect the hook reports. */
export function isExactReplayConflict(before: GuestCheckoutState, result: SubmitResult): boolean {
  return before.phase.name === "uncertain" && before.phase.checking && !before.priorSubmission &&
    result.kind === "refused" && result.failure.kind === "idempotency-conflict";
}

/** A Check answered with nothing definite: the uncertainty stays, its Check offered again. */
function stillUncertain(state: GuestCheckoutState, claimUntil: number): GuestCheckoutState {
  return { ...state, phase: { name: "uncertain", checking: false, checkAlsoLost: false, canCheck: true, claimUntil } };
}

/**
 * One reading of a submit or check answer. `wasCheck` changes only whether a
 * second lost answer is remembered.
 */
function afterSubmit(
  state: GuestCheckoutState,
  result: SubmitResult,
  webHost: string,
  wasCheck: boolean,
  claimUntil: number,
  /** A Check only: rc.11 1c's (i) and (ii) both hold (`replayIsDefinite`). */
  definite = false,
  /** When the answer came: a payment step whose checkout deadline has passed is not one to show. */
  now: number = Date.now()
): GuestCheckoutState {
  const submission = state.submission;
  if (!submission) return state;

  if (result.kind === "lost") {
    return {
      ...state,
      phase: { name: "uncertain", checking: false, checkAlsoLost: wasCheck, canCheck: true, claimUntil },
    };
  }

  if (result.kind === "answered") {
    const read = interpret(result.outcome, submission.expected, state.knownAppointmentId, now);
    // The gate first: a closed build folds this branch away (interpret cannot answer "pay" there).
    if (PAYMENTS_ENABLED && read.status === "pay") {
      return {
        ...state,
        priorSubmission: null,
        knownAppointmentId: result.outcome.appointment.id,
        phase: { name: "pay", start: read.start, outcome: read.outcome, managementUrl: acceptManagementUrl(result.managementUrl, webHost) },
      };
    }
    // A replay's CHECKOUT_EXPIRED that is not definite: the uncertainty, its
    // Check offered, the copy pay.return.waiting (useBooking waitingOr).
    if (PAYMENTS_ENABLED && !CHECKOUT_EXPIRED_REPLAY_IS_DEFINITE && read.status === "pay-waiting") {
      return { ...state, phase: { name: "uncertain", checking: false, checkAlsoLost: false, canCheck: true, claimUntil, payWaiting: true } };
    }
    if (read.status !== "done") return { ...state, phase: { name: "unauthorized" } };
    return {
      ...state,
      // The booking under this attempt's ONE key (SUBMIT_STARTED refuses any
      // other) answers for the original too: Backend's pre-read replays it.
      // Nothing is held any more (review of rc.13, P2).
      priorSubmission: null,
      knownAppointmentId: result.outcome.appointment.id,
      phase: {
        name: "done",
        kind: read.result.kind,
        outcome: read.result.outcome,
        managementUrl: acceptManagementUrl(result.managementUrl, webHost),
      },
    };
  }

  const failure = result.failure;
  /*
   * rc.11 1c: A CHECK (a replay) is read by the rule above. HOLD_GONE, a proof
   * refusal or a Terms refusal, when (i) and (ii) hold, is "no booking under
   * this key". Anything else, or too early, proves nothing: the uncertainty
   * stays. A conflict and an existing booking keep their own handling below.
   * One exception keeps rc.8's path: a Terms refusal while the hold is still
   * live goes back to Review's re-ask, the original kept (not a "none").
   */
  if (wasCheck && failure.kind !== "idempotency-conflict" && failure.kind !== "already-booked" && failure.kind !== "lost") {
    // Definite: nothing is under the key, so nothing held is kept. The SCREEN
    // follows the refusal's own kind, exactly as for a first send's answer
    // (hold-ended for HOLD_GONE, "try again later" for GUEST_BOOKING_UNAVAILABLE).
    if (definite && refusalCanBeDefinite(failure)) return afterSubmit({ ...state, priorSubmission: null }, result, webHost, false, claimUntil, false, now);
    if (!(failure.kind === "terms" && !state.holdExpired)) return stillUncertain(state, claimUntil);
  }
  // Rule 3: only these release the frozen submission, because each is a
  // documented "no appointment was written".
  const released = { ...state, submission: null };
  switch (failure.kind) {
    case "lost":
      return {
        ...state,
        phase: { name: "uncertain", checking: false, checkAlsoLost: wasCheck, canCheck: true, claimUntil },
      };
    case "guest-unverified":
      return { ...released, proof: null, phase: { name: "code", note: { kind: "unverified-at-submit" } } };
    case "guest-unavailable":
      return { ...released, phase: { name: "unavailable", reason: "guest-booking-unavailable" } };
    case "hold-gone":
      // rc.11: a hold-gone answering a NEW request says nothing about the
      // original one, which may have committed: its uncertainty, not hold-ended.
      if (state.priorSubmission) return uncertainOnPrior(released, state.priorSubmission, claimUntil);
      // Rule 6: the hold ended. Nobody said the time was taken. The proof was
      // not consumed (nothing was written), so it is kept for NEW_HOLD to judge.
      return { ...released, holdExpired: true, phase: state.proof ? { name: "ready" } : { name: "entry" } };
    case "already-booked":
      // The booking exists; the contract says to show it, not to retry. Keep
      // the submission: it is the only thing that could ever re-reach it.
      return { ...state, phase: { name: "booking-failed", failure } };
    case "idempotency-conflict":
      /*
       * A BOOKING EXISTS UNDER THIS KEY, from a different request. Never a
       * refusal, never "try again" under a new key. The may-have-gone-through
       * state, and its Check sends the ORIGINAL request when this page still
       * holds it (`priorSubmission`), which Backend replays; otherwise the
       * current one, and the state stays honest about not knowing.
       *
       * Except a CHECK answered this way with no original held: that is an
       * EXACT replay of the request the conflict should have matched, which
       * Backend calls a defect (rc.11 1c, in writing). The hook reports its
       * code alone (`isExactReplayConflict`). Never "none" and never "a
       * booking exists": the uncertainty, LOCKED (Get help), so no Check can
       * renew the identity claim press after press (security re-audit L2).
       */
      if (wasCheck && !state.priorSubmission) {
        return { ...state, phase: { name: "uncertain", checking: false, checkAlsoLost: false, canCheck: false, claimUntil: Number.NEGATIVE_INFINITY } };
      }
      return {
        ...state,
        submission: state.priorSubmission ?? submission,
        priorSubmission: null,
        phase: { name: "uncertain", checking: false, checkAlsoLost: wasCheck, canCheck: true, claimUntil },
      };
    case "contact-invalid":
      // Nothing was written, and the proof binds only the email, so the proof
      // and the key are KEPT. Back to verified-and-ready once the field is
      // corrected; a new code would spend the guest's code budget for nothing.
      return {
        ...released,
        contactRejection: { field: failure.field, detailCode: failure.detailCode },
        phase: state.proof ? { name: "ready" } : { name: "entry" },
      };
    case "terms":
      /*
       * THE TERMS, NOT THE BOOKING (Web Director, rc.8). Backend checks them
       * after the replay pre-read and after the proof check, which does not
       * consume the proof, and before any write. So: release the submission,
       * clear the decision, KEEP the key and the proof. The guest is shown the
       * Terms in force on Review, agrees again, and the next submission goes
       * under the same key and proof. If a lost first send did commit after
       * all, that submission replays it. Never re-sent on its own.
       */
      return {
        ...released,
        terms: null,
        policy: null,
        termsRefusal: { refusal: failure.refusal, afterCheck: wasCheck },
        // After a Check, the request that may have committed is kept: the
        // OLDEST one, never a newer request over it (rc.11, Web Director).
        priorSubmission: wasCheck ? (state.priorSubmission ?? submission) : state.priorSubmission,
        phase: state.proof ? { name: "ready" } : { name: "entry" },
      };
    default:
      return { ...released, phase: { name: "booking-failed", failure } };
  }
}

/** The events that begin a new tap's work, and so retire a session note. */
const CLEARS_SESSION_NOTE: ReadonlySet<Event["type"]> = new Set([
  "REQUEST_STARTED",
  "VERIFY_STARTED",
  "SUBMIT_STARTED",
  "CHECK_STARTED",
  "EDIT_EMAIL",
  "EDIT_CANCELLED",
  "NEW_HOLD",
  "TERMS_ACCEPTED",
]);

export function reduce(state: GuestCheckoutState, event: Event): GuestCheckoutState {
  const next = step(state, event);
  return next.sessionUnavailable && next !== state && CLEARS_SESSION_NOTE.has(event.type)
    ? { ...next, sessionUnavailable: false }
    : next;
}

function step(state: GuestCheckoutState, event: Event): GuestCheckoutState {
  switch (event.type) {
    case "REQUEST_STARTED": {
      if (isLocked(state)) return state;
      if (state.attempt && state.attempt !== event.attempt) return state; // one key per checkout
      // NO CODE BEFORE THE TERMS ARE DECIDED. The decision is made on Review
      // and travels with the first request; a resend or a changed address
      // keeps the one already held. Neither: nothing is requested.
      const terms = event.terms ?? state.terms;
      if (!terms) return state;
      // A different address is a new challenge; whatever was held is dead.
      const sameAddress = state.email === event.email;
      const next = sameAddress ? state : { ...state, challenge: null, proof: null };
      return {
        ...next,
        attempt: event.attempt,
        email: event.email,
        terms,
        // The policy goes with a decision made on Review; a resend keeps it.
        policy: event.terms ? event.policy ?? null : next.policy,
        policyChanged: event.terms ? false : next.policyChanged,
        termsRefusal: event.terms ? null : next.termsRefusal,
        phase: { name: "requesting", resend: next.challenge !== null },
      };
    }

    case "TERMS_ACCEPTED": {
      // To answer a Terms refusal (the proof still held), or a policy change
      // found at Verify (the challenge still held, rc.10 Q2), with nothing in
      // flight. No network: the next step is the code screen's Verify, a
      // separate press.
      const afterRefusal = state.phase.name === "ready" && state.proof !== null;
      const afterPolicyChange = state.policyChanged && ((state.phase.name === "code" && state.challenge !== null) || (state.phase.name === "ready" && state.proof !== null));
      if ((!afterRefusal && !afterPolicyChange) || state.submission || state.terms !== null) return state;
      return { ...state, terms: event.terms, policy: event.policy ?? null, policyChanged: false, termsRefusal: null };
    }

    case "POLICY_CHANGED":
      // Only on the code screen (a code to type, or a verified proof after a
      // Terms refusal), before anything was sent.
      if ((state.phase.name !== "code" && state.phase.name !== "ready") || state.submission || state.terms === null) return state;
      return { ...state, terms: null, policy: null, policyChanged: true };

    case "REQUEST_FINISHED":
      return state.phase.name === "requesting" ? settleExpiredPrior(afterRequest(state, event.call, event.now), event.now) : state;

    case "VERIFY_STARTED":
      // Never without the decision (and its policy) agreed on Review (rc.10).
      return state.phase.name === "code" && state.challenge && state.terms !== null ? { ...state, phase: { name: "verifying" } } : state;

    case "VERIFY_FINISHED":
      return state.phase.name === "verifying" ? settleExpiredPrior(afterVerify(state, event.call, event.now), event.now) : state;

    case "EDIT_EMAIL":
      // Only from the screens that offer it. A fail-closed or finished screen
      // must never reopen code requests, whatever a future control does.
      if (isLocked(state)) return state;
      // Not during an attempt pause: a new address is a way around it.
      if (isVerifyPaused(state, event.now)) return state;
      if (state.phase.name !== "entry" && state.phase.name !== "code" && state.phase.name !== "ready") return state;
      return { ...state, phase: { name: "change-email" } };

    case "EDIT_CANCELLED":
      if (state.phase.name !== "change-email") return state;
      return state.challenge
        ? codeWith(state, { kind: "none" })
        : state.proof
          ? { ...state, phase: { name: "ready" } }
          : { ...state, phase: { name: "entry" } };

    case "SUBMIT_STARTED":
      // Only from a verified, unlocked state, with a live hold, and never over
      // a live submission.
      if (state.phase.name !== "ready" || state.submission || !state.proof || state.holdExpired) return state;
      if (event.submission.input.guestContactVerificationId !== state.proof.verificationId) return state;
      if (!state.attempt || event.submission.input.idempotencyKey !== state.attempt.idempotencyKey) return state;
      // The submission carries EXACTLY the Terms decision the checkout holds:
      // the one made before the code was sent, or the one re-made after a
      // refusal. No decision, or a different one, and nothing is sent.
      if (!state.terms || !sameTerms(event.submission.input.termsAcceptance ?? null, state.terms.acceptance)) return state;
      return { ...state, submission: event.submission, phase: { name: "submitting" } };

    case "SUBMIT_FINISHED":
      return state.phase.name === "submitting"
        ? afterSubmit(state, event.result, event.webHost, false, event.now + UNKNOWN_OUTCOME_CLAIM_MS, false, event.now)
        : state;

    case "CHECK_STARTED": {
      const p = state.phase;
      if (p.name !== "uncertain" || !p.canCheck || p.checking || !state.submission) return state;
      // Each press renews the claim (N1): someone is here, asking.
      const checkSent = event.mono !== undefined && event.uid ? { mono: event.mono, at: event.now, uid: event.uid } : undefined;
      return { ...state, phase: { ...p, checking: true, claimUntil: event.now + UNKNOWN_OUTCOME_CLAIM_MS, checkSent } };
    }

    case "CHECK_FINISHED":
      if (state.phase.name !== "uncertain" || !state.phase.checking) return state;
      {
        const answered = afterSubmit(state, event.result, event.webHost, true, state.phase.claimUntil, replayIsDefinite(state, state.phase.checkSent), event.now);
        const lost = event.result.kind === "lost" || (event.result.kind === "refused" && event.result.failure.kind === "lost");
        if (!lost) return answered;
        // A lost Check is a send that may still commit (rc.11 1c): its own
        // threshold counts. Without a recorded instant, never definite again.
        const sent = state.phase.checkSent;
        return {
          ...answered,
          latestLostSend: sent
            ? { mono: sent.mono, at: sent.at, holdExpiresAt: state.submission?.sent?.holdExpiresAt ?? Number.NaN }
            : { mono: Number.NaN, at: Number.NaN, holdExpiresAt: Number.NaN },
        };
      }

    case "IDENTITY_LOST":
      // After dispatch the booking may exist and only this key can reach it —
      // and this key now belongs to no one. Say so; never mint a new one.
      // That includes the request a Check's Terms refusal released
      // (`priorSubmission`): it may have committed, so losing the identity
      // after it locks exactly as losing it mid-send does (review, rc.8).
      {
        const held = state.submission ?? state.priorSubmission;
        if (held) {
          // A newer request in flight is locked on; the original stays held
          // beside it, never dropped (rc.11).
          const kept = { ...state, submission: held, priorSubmission: state.submission ? state.priorSubmission : null };
          return state.phase.name === "uncertain"
            ? { ...kept, phase: { ...state.phase, checking: false, canCheck: false } }
            : { ...kept, phase: { name: "uncertain", checking: false, checkAlsoLost: false, canCheck: false, claimUntil: Number.NEGATIVE_INFINITY } };
        }
      }
      return { ...INITIAL, phase: { name: "identity-lost" } };

    case "START_FAILED":
      // Nothing was sent by the failed tap. With a checkout already under way
      // it is kept whole — key, challenge, proof — and the person tries again;
      // throwing it away would spend their resend limit for nothing.
      // An unknown outcome is locked, but its Check still needs to say why the
      // tap did nothing; the submission and the phase are untouched.
      if (state.phase.name === "uncertain") return { ...state, sessionUnavailable: true };
      if (isLocked(state)) return state;
      if (state.attempt) return { ...state, sessionUnavailable: true };
      return { ...state, phase: { name: "cannot-start" } };

    case "HOLD_EXPIRED": {
      // Before dispatch only. After dispatch the outcome is the server's to say.
      if (state.submission || state.phase.name === "done") return state;
      // rc.11: with the original held, the uncertainty, never hold-ended. In
      // flight (a code being asked for or checked), only the flag: the call's
      // landing settles it (settleExpiredPrior), and the screen guard holds
      // meanwhile.
      const p = state.phase.name;
      if (state.priorSubmission && p !== "requesting" && p !== "verifying") {
        return uncertainOnPrior(state, state.priorSubmission, event.now + UNKNOWN_OUTCOME_CLAIM_MS);
      }
      return { ...state, holdExpired: true };
    }

    case "NEW_HOLD": {
      if (!state.holdExpired || state.submission) return state;
      const proofAlive =
        state.proof !== null && event.now < Date.parse(state.proof.proofExpiresAt);
      if (event.policy.proofSurvivesNewHold && proofAlive) {
        return { ...state, holdExpired: false, policyChanged: false, phase: { name: "ready" } };
      }
      // Default: a new hold means a new PROOF. The key survives (rule 1); the
      // address stays so the person does not retype it.
      //
      // A code that was never verified is NOT spent, so its challenge is
      // kept: Backend holds the same challenge for the same address, and a
      // request inside `resendAfter` answers RESEND_TOO_SOON, which with a
      // challenge in hand returns the person to the code they already have.
      // Dropping it left them facing a wait with nothing to type (found wiring
      // the live route, 2026-09-22). A VERIFIED challenge was spent on the
      // proof, and a new code is the only way on.
      return {
        ...state,
        holdExpired: false,
        challenge: state.proof ? null : state.challenge,
        proof: null,
        // A new hold is a fresh start on Review: no stale "has changed".
        policyChanged: false,
        phase: { name: "entry" },
      };
    }

    case "DETAILS_CHANGED": {
      // Once anything has been dispatched, the frozen submission stands: the
      // address on it is the one that may already be booked. Before that, a
      // code or proof for the OLD address must not book the NEW one, so both
      // are dropped and the guest is back on Review. The key survives (rule 1).
      // (Review A2, 2026-09-22.)
      if (state.submission || state.email === null || state.email === event.email) return state;
      // Not during an attempt pause: a new address typed on Details would start
      // a fresh challenge beside the paused one, the same way round the pause
      // that EDIT_EMAIL refuses (review A2 on e85c040). The route keeps the
      // edit pending and re-applies it when the pause lifts.
      if (isVerifyPaused(state, event.now)) return state;
      const p = state.phase.name;
      if (p === "requesting" || p === "verifying" || p === "submitting" || p === "done") return state;
      return { ...state, email: null, challenge: null, proof: null, policyChanged: false, phase: { name: "entry" } };
    }

    case "CONTACT_REJECTION_SEEN":
      return state.contactRejection ? { ...state, contactRejection: null } : state;

    case "RESTART":
      if (isLocked(state)) return state;
      // rc.11 (Web Director, review P2): a fresh checkout mints a new key, and
      // the original may have committed under the old one. "Try again" goes
      // through its uncertainty instead, whose Check replays it. The hold is
      // left as it is: a restart says nothing about it. With a newer request
      // still held as well, nothing is dropped and nothing changes.
      if (state.priorSubmission) {
        if (state.submission) return state;
        return { ...uncertainOnPrior(state, state.priorSubmission, event.now + UNKNOWN_OUTCOME_CLAIM_MS), holdExpired: state.holdExpired };
      }
      return INITIAL;
  }
}

/**
 * WHICH SCREEN, derived. The hold-ended screen is a VIEW of `holdExpired`
 * rather than a phase, so an in-flight request can still land underneath it.
 */
export type Screen =
  | "review"
  | "code"
  | "change-email"
  | "submitting"
  | "uncertain"
  | "pay"
  | "done"
  | "hold-ended"
  | "unavailable"
  | "request-limited"
  | "request-problem"
  | "identity-lost"
  | "booking-failed"
  | "unauthorized"
  | "already-booked-unreachable"
  | "cannot-start";

export function screenOf(state: GuestCheckoutState): Screen {
  const p = state.phase;
  if (
    state.holdExpired &&
    !state.submission &&
    // rc.11: never while the original request may have committed.
    state.priorSubmission === null &&
    // After a Check, "No booking was made" may be false: Review keeps the
    // uncertainty notice and offers another time instead.
    !state.termsRefusal?.afterCheck &&
    p.name !== "requesting" &&
    p.name !== "verifying" &&
    p.name !== "unavailable" &&
    p.name !== "identity-lost"
  ) {
    return "hold-ended";
  }
  switch (p.name) {
    case "entry":
      return "review";
    case "requesting":
      return p.resend ? "code" : "review";
    case "request-lost":
    case "request-malformed":
      return "request-problem";
    case "request-limited":
      return "request-limited";
    case "ready":
      // Verified, but the Terms were refused and not yet agreed again: the
      // guest is shown them on Review. The proof is kept; no new code.
      return state.terms === null ? "review" : "code";
    case "code":
      // The policy changed before Verify: back to Review for a fresh Continue.
      return state.terms === null ? "review" : "code";
    case "verifying":
      return "code";
    case "change-email":
      return "change-email";
    case "submitting":
      return "submitting";
    case "uncertain":
      return "uncertain";
    case "pay":
      return "pay";
    case "done":
      return "done";
    case "unavailable":
      return "unavailable";
    case "identity-lost":
      return "identity-lost";
    case "booking-failed":
      return "booking-failed";
    case "unauthorized":
      return "unauthorized";
    case "already-booked-unreachable":
      return "already-booked-unreachable";
    case "cannot-start":
      return "cannot-start";
  }
}
